import { call, put, select, takeEvery } from 'redux-saga/effects';
import { createAction } from 'typesafe-actions';
import { BASKET_FLOW_UPDATE_ITEM, BasketFlowUpdateItemPayload } from 'features/basket';
import { EffectR } from 'types/utils';
import {
  selectProductDiscount,
  selectProductId,
  selectProductInfo,
  selectProductPricePrevious,
  selectReviewsTotalCount,
  selectReviewsTotalScore,
  selectVariantsRatingAvg,
  selectVariantsReviewsTotal,
} from './selectors';
import { NOT_SET } from '../utils';
import { GaProductBelong } from './types';
import {
  CompareCategory,
  getVariantsModel,
  isProductSet,
  selectCompareCategory,
  selectProductBrand,
  selectProductPriceCategory,
} from '../../product-info';
import { VariantsModel } from '../../variants';

/**
 * Cобытие должно срабатывать при добавлении товара в корзину.
 *
 * @see https://docs.google.com/document/d/1Y_vg_aaS7COYvoc4daxezKkWJUiH2Wn-hLAJ9D2ziac/edit#
 */
export const gaAddToCart = createAction('GA:addToCart')<{
  elementType: 'button';
  elementBlock: string; // блок, в котором расположен элемент
  elementName?: 'sticky-menu';
  productName: string; // название товара
  productId: string; // артикул товара
  productPrice: string; // стоимость единицы товара
  productQuantity: string; // на какое количество единиц изменилось количество товара
  productPosition?: string;
  productBelong: GaProductBelong; // принадлежность товара: LeroyMerlinMarketplace - для товаров Маркетплейс, Longtail - для товаров longtail, LeroyMerlin - для товаров Леруа Мерлен
  productPrevPrice: string | null;
  productDiscount: number | null;
  productTag?: string | null; // название шильда (бейджа) на товаре
  brand?: string | null;
  category?: string;
  komplektyId: string | null;
  is_variable: boolean;
  variant_selected: string | null;
  reviews: string; // количество отзывов
  avgRating: string; // рейтинг товара
  variantsReviewsTotal: string; // суммарное количество отзывов о всех вариантах товара
  variantsRatingAvg: string; // средний рейтинг среди всех вариантов товара
}>();

function* putGaAddToCart(
  productId: string,
  flowQty: number,
  context?: BasketFlowUpdateItemPayload['context'],
  isStickyMenu?: boolean,
) {
  const productQuantity = Number.isFinite(flowQty) ? Number(flowQty).toFixed(2) : NOT_SET;
  const productInfo = (yield selectProductInfo(context)) as EffectR.Call<typeof selectProductInfo>;
  const isSet: boolean = yield select(isProductSet);
  const variantsModelContainer: VariantsModel | null = yield select(getVariantsModel);
  const { variantsModel, activePoint } = variantsModelContainer ?? {};
  const isVariable = Boolean(variantsModel && variantsModel.length > 0);
  const variantSelected = isVariable && activePoint?.displayedName ? activePoint.displayedName : null;

  const brand = (yield call(selectProductBrand)) as string;
  const compareCategory = (yield call(selectCompareCategory)) as CompareCategory;
  const elementBlock = isSet ? 'komplektyDetail' : 'productDetail';
  const komplektyId: string | null = isSet ? yield call(selectProductId) : null;

  yield put(
    gaAddToCart({
      ...productInfo,
      elementBlock: context?.elementBlock ?? elementBlock,
      elementType: 'button',
      productId,
      productQuantity,
      productPrevPrice: yield call(selectProductPricePrevious),
      productDiscount: yield call(selectProductDiscount),
      productTag: yield call(selectProductPriceCategory),
      brand,
      category: compareCategory?.name || '',
      ...(isStickyMenu && { elementName: 'sticky-menu' }),
      komplektyId,
      is_variable: isVariable,
      variant_selected: variantSelected,
      avgRating: yield call(selectReviewsTotalScore),
      reviews: yield call(selectReviewsTotalCount),
      variantsReviewsTotal: yield call(selectVariantsReviewsTotal),
      variantsRatingAvg: yield call(selectVariantsRatingAvg),
    }),
  );
}

export function* runAddToCartDetector() {
  yield takeEvery(
    BASKET_FLOW_UPDATE_ITEM,
    function* handle({ payload }: { payload: BasketFlowUpdateItemPayload }) {
      const { productId, flowQty, context, isStickyMenu, isNeedGA = true } = payload;
      if (flowQty > 0 && isNeedGA) {
        yield putGaAddToCart(productId, flowQty, context, isStickyMenu);
      }
    },
  );
}
